import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Alert, LinearProgress, GridLegacy as Grid } from '@mui/material';
import { Page, Row } from '../../util/Page';
import EquipmentDataTable from '../elements/EquipmentDataTable';
import BuildingStagingLoader from '../../loaders/BuildingStagingLoader';
import getBuildingStaging from '../../../redux/selectors/staging';
import EquipmentLoader from '../../loaders/EquipmentLoader';
import LocationLoader from '../../loaders/LocationLoader';
import { DeploymentsLoader } from '../../loaders/deployments';
import BooleanFilterOption from '../elements/filter/BooleanFilterOption';
import StagingFilterBuilder, { FilterDefinition } from '../elements/StagingFilterBuilder';
import SliderFilterPopover, { SliderOptions } from '../elements/filter/SliderFilterPopover';
import TextFilterPopover from '../elements/filter/TextFilterPopover';
import EquipmentTypeFilterPopover from '../elements/filter/EquipmentTypeFilterPopover';
import {
  EquipmentTypeFilter,
  ContainsTextFilter,
  RangeFilter,
  BooleanFilter,
} from '../../../redux/constants/filters';

import PromotionResultDialog from '../elements/dialogs/PromotionResultDialog';
import { FilterProvider } from '../contexts/FilterContext';
import { setPage } from '../../../redux/actions/staging';

const EquipmentView = ({ buildingId, loaded, equipment, points, buildingError, resetPage }) => {
  const [promotionResults, setPromotionResults] = useState(undefined);
  const [selectedEquipIds, setSelectedEquipIds] = useState([]);

  useEffect(() => resetPage, [resetPage]);

  // reset selections on building change
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => setSelectedEquipIds([]), [buildingId, setSelectedEquipIds]);

  const onPromotionAction = (result) => {
    if (!result) return;
    setPromotionResults(result);
  };

  const onEquipmentSelected = (newEquipIds) => {
    setSelectedEquipIds(newEquipIds);
  };

  function extractDevice(equip, extractFn) {
    return (equip.devices ?? []).map((device) => extractFn(device));
  }

  const filterDefs = [
    new FilterDefinition(
      BooleanFilterOption,
      'hide_valid',
      'Invalid Status',
      {},
      () =>
        new BooleanFilter({
          key: 'hide_valid',
          getter: (e) => e,
          evaluation: (e) =>
            e.validation === undefined ||
            e.validation.length !== 0 ||
            e.points
              ?.flatMap((point) => point.validation === undefined || point.validation.length !== 0)
              .filter(Boolean).length !== 0,
        }),
      (val) => val
    ),
    new FilterDefinition(
      EquipmentTypeFilterPopover,
      'equip_type',
      'Equip Type',
      {},
      (type) =>
        new EquipmentTypeFilter({
          key: 'equip_type',
          getter: (e) => e.equipment_type_id,
          type,
        })
    ),
    new FilterDefinition(
      SliderFilterPopover,
      'equipConfidence',
      'Equip Type Confidence',
      new SliderOptions([0, 100], [50, 100]),
      (v) =>
        new RangeFilter({
          key: 'equipConfidence',
          getter: (e) => e.confidences.equipment_type_id,
          minimum: v[0],
          maximum: v[1],
        })
    ),
    new FilterDefinition(
      TextFilterPopover,
      'equip_name',
      'Equip Name',
      {},
      (v) => new ContainsTextFilter({ key: 'equip_name', getter: (e) => e.name, fragment: v })
    ),
    new FilterDefinition(
      TextFilterPopover,
      'device_id',
      'Device ID',
      {},
      (v) =>
        new ContainsTextFilter({
          key: 'device_id',
          getter: (equip) => extractDevice(equip, (d) => d.device_id).join(' '),
          fragment: v,
        })
    ),
    new FilterDefinition(
      TextFilterPopover,
      'object_name',
      'Device Name',
      {},
      (v) =>
        new ContainsTextFilter({
          key: 'object_name',
          getter: (equip) => extractDevice(equip, (d) => d.name).join(' '),
          fragment: v,
        })
    ),
    new FilterDefinition(
      TextFilterPopover,
      'model_name',
      'Device Model',
      {},
      (v) =>
        new ContainsTextFilter({
          key: 'model_name',
          getter: (equip) => extractDevice(equip, (d) => d.properties?.modelName).join(' '),
          fragment: v,
        })
    ),
    new FilterDefinition(
      BooleanFilterOption,
      'hide_parents',
      'Empty Equipment Only',
      {},
      () =>
        new BooleanFilter({
          key: 'hide_parents',
          getter: (e) => e.points,
          evaluation: (p) => p.length === 0,
        }),
      (val) => val
    ),
  ];

  if (!loaded) {
    return (
      <Page>
        <Row>
          <EquipmentLoader key={buildingId} buildingId={buildingId} />
          <BuildingStagingLoader key={`stg-${buildingId}`} buildingId={buildingId} />
          <LocationLoader key={`ll-${buildingId}`} buildingId={buildingId} />
          <DeploymentsLoader key="deployments" />
          <LinearProgress color="secondary" className="loading" />
        </Row>
      </Page>
    );
  }

  return (
    <FilterProvider onFilterChange={resetPage}>
      {buildingError?.message ? (
        <Grid item xs={12}>
          <Alert severity="error">
            Errors detected with promotion:
            <p />
            {buildingError?.message}
          </Alert>
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <StagingFilterBuilder filterDefs={filterDefs} />
      </Grid>
      <Grid item xs={12}>
        <EquipmentDataTable
          buildingId={buildingId}
          onPromotionResults={onPromotionAction}
          selectedEquipIds={selectedEquipIds}
          onItemSelected={onEquipmentSelected}
          equipment={equipment}
          points={points}
        />
      </Grid>
      <PromotionResultDialog
        equipment={equipment}
        promotionResults={promotionResults}
        open={promotionResults !== undefined}
        onClose={() => setPromotionResults(undefined)}
      />
    </FilterProvider>
  );
};

const mapStateToProps = (state, { buildingId }) => ({
  ...getBuildingStaging(state, buildingId),
  loaded: state.entities.staging.loaded[buildingId] ?? false,
  buildingId,
});

const mapDispatchToProps = (dispatch) => ({
  resetPage: () => dispatch(setPage(0)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EquipmentView);
